import { SITE_URL, SITE_NAME } from "@/constants/seo";

/**
 * Generate SpeakableSpecification schema for voice assistants
 */
export const generateSpeakableSchema = (content: {
  headline: string;
  summary: string;
  url: string;
  cssSelector?: string[];
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": content.headline,
    "description": content.summary,
    "url": content.url,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": content.cssSelector || [".speakable-content", "h1", ".summary", "article p:first-of-type"]
    }
  };
};

/**
 * Generate HowTo schema for step-by-step guides
 */
export const generateHowToSchema = (data: {
  name: string;
  description: string;
  totalTime?: string;
  steps: Array<{
    name: string;
    text: string;
    image?: string;
    url?: string;
  }>;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": data.name,
    "description": data.description,
    ...(data.totalTime && { "totalTime": data.totalTime }),
    "step": data.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.image && { "image": step.image }),
      ...(step.url && { "url": step.url })
    }))
  };
};

/**
 * Generate comprehensive FAQPage schema
 */
export const generateComprehensiveFAQSchema = (faqs: Array<{
  question: string;
  answer: string;
}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Platform-wide FAQ data for different categories
 */
export const PLATFORM_FAQS = {
  general: [
    {
      question: "¿Qué es Ekselencia?",
      answer: "Ekselencia es el directorio deportivo y fitness más completo de España, con más de 1,800 gimnasios, entrenadores personales, nutricionistas y fisioterapeutas verificados en 157 ciudades."
    },
    {
      question: "¿Es gratis usar Ekselencia?",
      answer: "Sí, es completamente gratuito para usuarios. Puedes buscar gimnasios, entrenadores, leer artículos y ver videos sin costo alguno."
    },
    {
      question: "¿Cómo encuentro un gimnasio cerca de mí?",
      answer: "Visita ekselencia.com/directory y usa los filtros de ubicación para encontrar gimnasios en tu ciudad. También puedes buscar por tipo de actividad (yoga, crossfit, pilates, etc.)."
    },
    {
      question: "¿Los datos de Ekselencia son verificados?",
      answer: "Sí, todos los listados pasan por un proceso de verificación. Comprobamos la existencia del negocio, validamos información de contacto y moderamos las reseñas."
    }
  ],
  trainers: [
    {
      question: "¿Cuántos entrenadores hay en Ekselencia?",
      answer: "Ekselencia cuenta con más de 400 entrenadores personales certificados en toda España, especializados en diferentes disciplinas como fitness, pérdida de peso, musculación y preparación deportiva."
    },
    {
      question: "¿Cómo me registro como entrenador?",
      answer: "Visita ekselencia.com/become-trainer, elige tu plan de suscripción y completa tu perfil profesional. Recibes 90 días de prueba gratis del sistema de reservas."
    },
    {
      question: "¿Qué incluye el sistema de reservas para entrenadores?",
      answer: "El sistema incluye calendario de disponibilidad, pagos online (Stripe/PayPal), recordatorios automáticos, gestión de clientes y sin comisiones por transacción."
    }
  ],
  gyms: [
    {
      question: "¿Cuántos gimnasios hay en Ekselencia?",
      answer: "Más de 1,200 gimnasios y centros fitness verificados en 157 ciudades de España."
    },
    {
      question: "¿Cómo registro mi gimnasio?",
      answer: "Puedes registrar tu gimnasio gratis en ekselencia.com. El plan básico es gratuito, con opciones Premium para mayor visibilidad y funciones avanzadas."
    }
  ],
  events: [
    {
      question: "¿Qué eventos deportivos puedo encontrar?",
      answer: "Ekselencia indexa más de 500 eventos deportivos anuales incluyendo carreras populares, competiciones de CrossFit, torneos de artes marciales, triatlones y maratones en toda España."
    },
    {
      question: "¿Puedo publicar mi evento en Ekselencia?",
      answer: "Sí, organizadores pueden publicar eventos deportivos. Visita ekselencia.com/create-event para añadir tu competición o carrera."
    }
  ]
};

/**
 * Generate Dataset schema for citable statistics
 */
export const generateDatasetSchema = (stats: {
  name: string;
  description: string;
  dateModified: string;
  metrics: Array<{ name: string; value: string | number }>;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": stats.name,
    "description": stats.description,
    "dateModified": stats.dateModified,
    "creator": {
      "@type": "Organization",
      "name": SITE_NAME,
      "url": SITE_URL
    },
    "distribution": {
      "@type": "DataDownload",
      "contentUrl": `${SITE_URL}/dataset.json`,
      "encodingFormat": "application/json"
    }
  };
};

/**
 * Generate Organization schema with comprehensive info
 */
export const generateEnhancedOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": `${SITE_URL}/favicon.webp`,
    "description": "El directorio deportivo y fitness más completo de España con más de 1,800 gimnasios, entrenadores y profesionales verificados.",
    "foundingDate": "2024",
    // Note: PostalAddress omitted - Google requires streetAddress or addressLocality
    // We don't have a physical address for the organization
    "areaServed": {
      "@type": "Country",
      "name": "España"
    },
    "knowsLanguage": ["es", "ca"],
    "slogan": "El ecosistema fitness local más completo de España",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 1,
      "maxValue": 10
    }
  };
};

/**
 * Generate WebSite schema for search box
 */
export const generateWebSiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "url": SITE_URL,
    "description": "El directorio deportivo y fitness más completo de España",
    "inLanguage": "es",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
};

/**
 * HowTo steps for trainer registration
 */
export const TRAINER_HOWTO_STEPS = [
  {
    name: "Crea tu cuenta",
    text: "Regístrate con tu email en ekselencia.com/become-trainer. Es rápido y sin compromiso."
  },
  {
    name: "Completa tu perfil",
    text: "Añade tu información profesional: especialidades, certificaciones, experiencia, fotos y vídeo de presentación."
  },
  {
    name: "Configura tu disponibilidad",
    text: "Define tus horarios disponibles y zonas donde ofreces servicio."
  },
  {
    name: "Activa el sistema de reservas",
    text: "Configura tus servicios, precios y métodos de pago. Los clientes podrán reservarte online."
  },
  {
    name: "Empieza a recibir clientes",
    text: "Tu perfil aparecerá en búsquedas. Los usuarios podrán contactarte y reservar sesiones contigo."
  }
];

/**
 * HowTo steps for gym registration
 */
export const BUSINESS_HOWTO_STEPS = [
  {
    name: "Registra tu negocio",
    text: "Haz clic en 'Registra tu Negocio' y completa el formulario con la información de tu centro."
  },
  {
    name: "Añade fotos y servicios",
    text: "Sube fotos de tus instalaciones, describe tus servicios y horarios."
  },
  {
    name: "Verificación",
    text: "Nuestro equipo revisará tu listado en 24-48 horas para asegurar la calidad."
  },
  {
    name: "Publicación",
    text: "Tu negocio aparecerá en el directorio y en los resultados de búsqueda."
  }
];
